var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useCallback } from 'react';
import styled, { css } from '../../styled';
import * as Tooltip from '../tooltip';
import TooltipView from '../tooltip/TooltipView';
import { StyledInput as InputComponent, InputWrapper, MessageWrapper, Message, RightIconWrapper, generateStatusStyle, } from '../input';
const Wrapper = styled.div `
  display: ${({ block }) => (block ? 'block' : 'inline-block')};
  border-radius: ${({ borderRadius }) => borderRadius || '6px'};
  outline: 0;
  ${({ status, disabled, focused }) => generateStatusStyle(status, true, disabled, focused)};
  input {
    border: none !important;
  }
  .inputWithIcon-tooltip {
    width: 100%;
  }
`;
const StyledInput = styled(InputComponent) `
  ${({ borderRadius }) => borderRadius && `border-radius: ${borderRadius}`};
  padding-left: 0.75rem;
  padding-right: 2.5rem;
`;
const StyledRightIconWrapper = styled(RightIconWrapper) `
  padding: 0;
  line-height: unset;

  // Override Safari disabled font color
  > div > div > span {
    -webkit-text-fill-color: ${({ theme }) => theme.colors.SNOW_100};
  }
`;
const ButtonWrapper = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0 5px 5px 0;
  width: 2.25rem;
  height: 2.25rem;
  border-left: solid 0.0625rem ${({ theme }) => theme.colors.SKY_DARK};

  ${({ disabled, theme }) => disabled
    ? css `
          cursor: not-allowed;
          background-color: ${theme.colors[theme.inputs.status.disabled.backgroundColor]};
          border-left: 0.0625rem solid
            ${theme.colors[theme.inputs.status.disabled.borderColor]};
          & svg > path {
            fill: ${theme.colors.INK_300};
          }
        `
    : css `
          &:hover {
            background-color: ${theme.colors.PRIMARY_100};
            border-left: 0.0625rem solid
              ${theme.colors[theme.inputs.status.hover.borderColor]};
          }
          &:active {
            background-color: ${theme.colors.PRIMARY_300};
            border-left: 0.0625rem solid
              ${theme.colors[theme.inputs.status.hover.borderColor]};
          }
          &:hover svg > path {
            fill: ${theme.colors.PRIMARY_600};
          }

          &:active svg > path {
            fill: ${theme.colors.PRIMARY_700};
          }
        `}
`;
const StyledMessage = styled(Message) `
  font-size: 0.875rem;
  color: ${({ theme, status }) => status === 'invalid' ? theme.colors.RED_700 : theme.colors.GREEN_600};
`;
const InputWithSuffixIcon = (_a) => {
    var { disabled, status = 'valid', value, renderMessages, innerRef = React.createRef(), block = false, Icon, inputSize = 'MEDIUM', borderRadius, onChange, onButtonClick, onFocus, onBlur, onMouseEnter, onMouseLeave, onKeyPress, isButtonDisabled = false, isInputTooltipShow, tooltipText, tooltipsDirection = 'TOP_CENTER', buttonTooltipLabel, e2eInputId, e2eMessagesId } = _a, restProps = __rest(_a, ["disabled", "status", "value", "renderMessages", "innerRef", "block", "Icon", "inputSize", "borderRadius", "onChange", "onButtonClick", "onFocus", "onBlur", "onMouseEnter", "onMouseLeave", "onKeyPress", "isButtonDisabled", "isInputTooltipShow", "tooltipText", "tooltipsDirection", "buttonTooltipLabel", "e2eInputId", "e2eMessagesId"]);
    const [isFocus, setIsFocus] = useState(false);
    const getRefCurrent = useCallback(() => {
        if (typeof innerRef === 'function' || !innerRef || !innerRef.current)
            return null;
        return innerRef.current;
    }, [innerRef]);
    const handleOnChange = useCallback((event) => {
        if (onChange) {
            onChange(event.target.value);
        }
    }, [onChange]);
    const handleFocus = useCallback((event) => {
        setIsFocus(true);
        if (onFocus)
            onFocus(event);
    }, [setIsFocus, onFocus]);
    const handleBlur = useCallback((event) => {
        setIsFocus(false);
        if (onBlur)
            onBlur(event);
    }, [setIsFocus, onBlur]);
    const handleMouseEnter = useCallback((event) => {
        if (onMouseEnter)
            onMouseEnter(event);
    }, [onMouseEnter]);
    const handleMouseLeave = useCallback((event) => {
        if (onMouseLeave)
            onMouseLeave(event);
    }, [onMouseLeave]);
    const handleButtonClick = useCallback((event) => {
        const refCurrent = getRefCurrent();
        if (onButtonClick)
            onButtonClick(event, (refCurrent === null || refCurrent === void 0 ? void 0 : refCurrent.value) || '');
    }, [getRefCurrent, onButtonClick]);
    const handleKeyPress = useCallback((event) => {
        const refCurrent = getRefCurrent();
        if (onKeyPress)
            onKeyPress(event, (refCurrent === null || refCurrent === void 0 ? void 0 : refCurrent.value) || '');
    }, [getRefCurrent, onKeyPress]);
    return (React.createElement("div", null,
        React.createElement(Wrapper, { status: status, block: block, disabled: disabled, focused: isFocus, borderRadius: borderRadius },
            React.createElement(InputWrapper, { "data-e2e-id": e2eInputId, block: block },
                React.createElement(TooltipView, { type: "primary", lang: "en", text: tooltipText ? tooltipText.toString() : '', show: isInputTooltipShow, direction: tooltipsDirection, containerClassName: "inputWithIcon-tooltip" },
                    React.createElement(StyledInput, Object.assign({}, restProps, { ref: innerRef, borderRadius: borderRadius, value: value, type: "input", status: status, disabled: disabled, inputSize: inputSize, block: block, haveRightIcon: !!Icon, haveLeftIcon: false, onChange: handleOnChange, onFocus: handleFocus, onBlur: handleBlur, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, onKeyPress: handleKeyPress }))),
                React.createElement(StyledRightIconWrapper, { status: status, inputSize: inputSize },
                    React.createElement(Tooltip.Primary, { direction: tooltipsDirection, text: buttonTooltipLabel }, Icon && (React.createElement(ButtonWrapper, { inputSize: inputSize, onClick: handleButtonClick, disabled: isButtonDisabled },
                        React.createElement(Icon, { color: "INK_600", size: "MEDIUM" }))))))),
        renderMessages && (React.createElement(MessageWrapper, { "data-e2e-id": e2eMessagesId },
            React.createElement(StyledMessage, { status: status }, renderMessages())))));
};
export default React.forwardRef((props, ref) => (React.createElement(InputWithSuffixIcon, Object.assign({ innerRef: ref || React.createRef() }, props))));
//# sourceMappingURL=index.js.map